import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import TableContainer from '@mui/material/TableContainer';
import ActionIconButton from '@components/ActionIconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/EditRounded';
import { Table, getPaginationLabels } from '@components/Table';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { useTranslation, useLocations } from '@hooks';
import { formatAssignedEntities } from '@helpers/formatEntitiesToString';
import { EntityChip, ChipsCell } from '@components/EntityChips';
import { KNOWLEDGE_BASE_TYPE_LABELS, KnowledgeBaseType } from '@constants/knowledge-base';
import { agentsWithRoom, everyAgentAtLimit } from '@helpers/knowledge-base';
import { FORM_TYPE } from '../EditModal';
import DocumentStatusChip from '../DocumentStatusChip';

const TitleCell = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
});

const ActionSpacer = styled('span')({
    display: 'inline-block',
    width: 36,
});

const CroppedText = styled(Typography)({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 400,
    '@media (max-width: 1024px)': {
        maxWidth: 200,
    },
});

const getTableHeader = (t, hasSingleLocation) => {
    return [
        {
            key: 0,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.title')}
                </Typography>
            ),
        },
        {
            key: 1,
            width: 110,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.type')}
                </Typography>
            ),
        },
        {
            key: 2,
            width: 300,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('KnowledgeBase.agents')}
                </Typography>
            ),
        },
        ...(hasSingleLocation
            ? []
            : [
                  {
                      key: 3,
                      width: 240,
                      component: (
                          <Typography align="left" variant="subtitle2">
                              {t('common.locations')}
                          </Typography>
                      ),
                  },
              ]),
        {
            key: 4,
            width: 130,
            component: <Typography align="left" variant="subtitle2"></Typography>,
        },
    ];
};

const renderAgentsCell = (agents, assignedIds) => {
    const assignedAgents = agents.filter(agent => (assignedIds || []).includes(agent.id));
    if (!assignedAgents.length) {
        return (
            <ChipsCell>
                <Typography variant="body2">--</Typography>
            </ChipsCell>
        );
    }
    return (
        <ChipsCell>
            {assignedAgents.map(agent => (
                <EntityChip key={agent.id} label={agent.name} />
            ))}
        </ChipsCell>
    );
};

const getTableBody = (data, onClickAction, locations, agents, t, canWrite, hasSingleLocation) => {
    return data.map(content => {
        const canDuplicate = content.type !== KnowledgeBaseType.DOCUMENT && !everyAgentAtLimit(agents, content.type);
        return [
            {
                component: (
                    <TitleCell>
                        <Typography variant="subtitle2" align="left">
                            {content.title}
                        </Typography>
                        {content.type === KnowledgeBaseType.DOCUMENT && <DocumentStatusChip status={content.status} />}
                    </TitleCell>
                ),
            },
            {
                component: (
                    <CroppedText variant="body2" align="left">
                        {t(KNOWLEDGE_BASE_TYPE_LABELS[content.type])}
                    </CroppedText>
                ),
            },
            {
                component: renderAgentsCell(agents, content.agents),
            },
            ...(hasSingleLocation
                ? []
                : [
                      {
                          component: (
                              <Typography variant="body2" align="left">
                                  {content.locations.length > 0
                                      ? formatAssignedEntities(locations, content.locations, {
                                            idKey: 'companyId',
                                            labelKey: 'internalName',
                                            limit: 30,
                                        })
                                      : t('common.allLocations')}
                              </Typography>
                          ),
                      },
                  ]),
            ...(canWrite
                ? [
                      {
                          component: (
                              <Typography align="right" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <ActionIconButton
                                      title={t('common.edit')}
                                      onClick={() => onClickAction(content, FORM_TYPE.EDIT)}
                                  >
                                      <EditIcon viewBox="0 0 22 22" />
                                  </ActionIconButton>
                                  {canDuplicate ? (
                                      <ActionIconButton
                                          title={t('common.duplicate')}
                                          sx={{ width: '36px' }}
                                          onClick={() => onClickAction(content, FORM_TYPE.EDIT_COPY)}
                                      >
                                          <ContentCopyIcon sx={{ fontSize: 18 }} />
                                      </ActionIconButton>
                                  ) : (
                                      <ActionSpacer />
                                  )}
                                  <ActionIconButton
                                      title={t('common.delete')}
                                      onClick={() => onClickAction(content, FORM_TYPE.DELETE)}
                                  >
                                      <DeleteIcon sx={{ path: { fill: 'rgba(0,0,0,.6)' } }} />
                                  </ActionIconButton>
                              </Typography>
                          ),
                      },
                  ]
                : [
                      {
                          component: (
                              <Typography align="right">
                                  <ActionIconButton
                                      title={t('common.view')}
                                      onClick={() => onClickAction(content, FORM_TYPE.VIEW)}
                                  >
                                      <VisibilityIcon viewBox="0 0 22 22" />
                                  </ActionIconButton>
                              </Typography>
                          ),
                      },
                  ]),
        ];
    });
};

const KnowledgeBaseTable = ({
    setModal,
    setSelectedContent,
    onPageChange,
    locations,
    page,
    onRowsPerPageChange,
    rowsPerPage,
    setDeleteModalOpen,
    canWrite,
    selectedItems,
    setSelectedItems,
}) => {
    const { t } = useTranslation();
    const { hasSingleLocation } = useLocations();
    const { data, loading, totalCount, pages } = useSelector(state => state.knowledgeBase);
    const agents = useSelector(state => state.agents.data);

    const onClickAction = (content, type) => {
        setSelectedContent({
            ...content,
            ...(type === FORM_TYPE.EDIT_COPY && {
                name: `${content.name} (${t('common.copy')})`,
                agents: agentsWithRoom(content.agents, agents, content.type),
            }),
        });
        if (type === FORM_TYPE.DELETE) {
            setDeleteModalOpen(true);
            return;
        }
        setModal({ open: true, type });
    };

    const columnNames = getTableHeader(t, hasSingleLocation);

    return (
        <TableContainer>
            <Table
                withHover
                columnNames={columnNames}
                data={data}
                rows={getTableBody(data, onClickAction, locations, agents, t, canWrite, hasSingleLocation)}
                onRowClick={content => onClickAction(content, canWrite ? FORM_TYPE.EDIT : FORM_TYPE.VIEW)}
                isDataLoading={loading}
                withCheckbox={canWrite}
                entity={t('KnowledgeBase.items')}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                withPagination
                paginationProps={{
                    colSpan: columnNames.length + (canWrite ? 1 : 0),
                    count: totalCount,
                    rowsPerPage,
                    page,
                    onPageChange,
                    onRowsPerPageChange,
                    ...getPaginationLabels(t),
                    pages,
                }}
            />
        </TableContainer>
    );
};

export default KnowledgeBaseTable;
